import { useEffect, useState } from "react";
import { api, getToken } from "@/lib/api";
import { formatFull } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import InvestorForm from "@/components/InvestorForm";
import ConsultationList from "@/components/ConsultationList";
import SendEmailDialog from "@/components/SendEmailDialog";
import { ALL_COUNTRIES, PROPERTY_TYPES, INVESTOR_STATUS } from "@/lib/constants";
import { toast } from "sonner";
import { MapPin, ShieldCheck, ShieldAlert, Wallet, Clock, Plus, Pencil, Trash2, Send, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function splitName(name = "") {
  const parts = name.trim().split(" ");
  return { first: parts[0] || name, last: parts.slice(1).join(" ") };
}

const CONTACT_STYLES = {
  Actif: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "En discussion": "bg-amber-50 text-amber-700 border-amber-200",
  Prospect: "border-slate-200 text-slate-600",
};

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 w-full sm:w-40" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid={`${testid}-all`}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Investors() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [ndas, setNdas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [nda, setNda] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [opps, setOpps] = useState([]);
  const [msStatus, setMsStatus] = useState({ configured: false, connected: false, email: null });
  const [teaserFor, setTeaserFor] = useState(null);
  const [teaserOppId, setTeaserOppId] = useState("");
  const [creatingTeaser, setCreatingTeaser] = useState(false);
  const [emailNda, setEmailNda] = useState(null);

  const load = () => api.get("/investors").then((r) => setItems(r.data));
  const loadNdas = () => api.get("/ndas").then((r) => setNdas(r.data)).catch(() => {});
  const loadMsStatus = () => api.get("/microsoft/status").then((r) => setMsStatus(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadNdas();
    loadMsStatus();
    api.get("/opportunities").then((r) => setOpps(r.data)).catch(() => {});
  }, []);

  const connectMicrosoft = () => {
    const base = encodeURIComponent(window.location.origin);
    window.location.href = `${window.location.origin}/api/microsoft/login?auth=${getToken()}&redirect_base=${base}`;
  };

  const openTeaser = (inv) => {
    setSelected(null);
    setTeaserFor(inv);
    setTeaserOppId("");
  };

  const startTeaser = async () => {
    if (!teaserOppId) return toast.error(t("investors.toast.selectOpp"));
    setCreatingTeaser(true);
    const { first, last } = splitName(teaserFor.name);
    try {
      const r = await api.post("/ndas", {
        first_name: first,
        last_name: last,
        company: teaserFor.name || "",
        email: teaserFor.email || "",
        phone: teaserFor.phone || "",
        opportunity_id: teaserOppId,
        investor_id: teaserFor.id,
      });
      setTeaserFor(null);
      setEmailNda(r.data);
      loadNdas();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("investors.toast.teaserError"));
    } finally {
      setCreatingTeaser(false);
    }
  };

  const investorNdas = (inv) =>
    ndas.filter((n) => n.investor_id === inv.id || (n.company && inv.name && n.company === inv.name));

  const filtered = items.filter(
    (inv) =>
      (country === "all" || inv.country === country) &&
      (status === "all" || inv.status === status) &&
      (type === "all" || inv.types?.includes(type)) &&
      (nda === "all" || (nda === "signed" ? inv.nda_signed : !inv.nda_signed)) &&
      (inv.name.toLowerCase().includes(q.toLowerCase()) ||
        (inv.email || "").toLowerCase().includes(q.toLowerCase()) ||
        (inv.phone || "").toLowerCase().includes(q.toLowerCase()) ||
        inv.country.toLowerCase().includes(q.toLowerCase()))
  );

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (e, inv) => {
    e.stopPropagation();
    setEditing(inv);
    setFormOpen(true);
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/investors/${deleting.id}`);
      toast.success(t("investors.toast.deleted"));
      setDeleting(null);
      load();
    } catch {
      toast.error(t("investors.toast.deleteError"));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="investors-page">
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="overline text-[#C6A87C]">{t("investors.overline")}</p>
          <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">{t("investors.title")}</h1>
          <p className="mt-2 text-slate-500">{t("investors.subtitle", { count: items.length })}</p>
        </div>
        <Button onClick={openAdd} data-testid="add-investor-btn" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
          <Plus className="mr-2 h-4 w-4" /> {t("investors.add")}
        </Button>
      </header>

      <div className="mb-8 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Input
          data-testid="investors-search"
          placeholder={t("investors.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 lg:w-72"
        />
        <div className="flex flex-wrap gap-3">
          <FilterSelect value={country} onChange={setCountry} options={ALL_COUNTRIES} placeholder={t("common.allCountries")} testid="inv-filter-country" />
          <FilterSelect value={type} onChange={setType} options={PROPERTY_TYPES} placeholder={t("common.allTypes")} testid="inv-filter-type" />
          <FilterSelect value={status} onChange={setStatus} options={INVESTOR_STATUS} placeholder={t("common.allStatuses")} testid="inv-filter-status" />
          <Select value={nda} onValueChange={setNda}>
            <SelectTrigger className="h-10 w-full sm:w-40" data-testid="inv-filter-nda"><SelectValue placeholder="NDA" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("investors.allNda")}</SelectItem>
              <SelectItem value="signed">{t("investors.ndaSigned")}</SelectItem>
              <SelectItem value="unsigned">{t("investors.ndaUnsigned")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((inv, i) => (
          <motion.div
            key={inv.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.4 }}
            data-testid={`investor-card-${inv.id}`}
            onClick={() => setSelected(inv)}
            className="group relative cursor-pointer rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
          >
            <div className="absolute right-4 top-4 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
              <button onClick={(e) => openEdit(e, inv)} data-testid={`edit-investor-${inv.id}`} className="text-slate-400 hover:text-[#0F172A]">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setDeleting(inv); }} data-testid={`delete-investor-${inv.id}`} className="text-slate-400 hover:text-red-600">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-start gap-4">
              <img src={inv.avatar} alt={inv.name} className="h-14 w-14 rounded-full object-cover" />
              <div className="min-w-0 flex-1 pr-10">
                <h3 className="truncate font-display text-xl leading-tight text-[#0F172A]">{inv.name}</h3>
                <p className="truncate text-sm text-slate-500" data-testid={`inv-card-email-${inv.id}`}>{inv.email || "—"}</p>
                {inv.phone && <p className="truncate text-xs text-slate-400">{inv.phone}</p>}
                <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                  <MapPin className="h-3 w-3" /> {inv.country}
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-xl bg-slate-50 px-4 py-3">
              <p className="overline text-slate-400">{t("investors.budget")}</p>
              <p className="flex items-center gap-1.5 font-semibold text-[#0F172A]">
                <Wallet className="h-4 w-4 text-[#C6A87C]" />
                {formatFull(inv.budget_min, inv.currency)} – {formatFull(inv.budget_max, inv.currency)}
              </p>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {(inv.types || []).map((t) => (
                <Badge key={t} variant="outline" className="rounded-full border-slate-200 text-xs text-slate-600">{t}</Badge>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <Badge variant="outline" className={`rounded-full ${CONTACT_STYLES[inv.status] || ""}`}>{inv.status}</Badge>
              <span className="flex items-center gap-1 text-xs text-slate-400">
                {inv.nda_signed ? <ShieldCheck className="h-3.5 w-3.5 text-[#C6A87C]" /> : <ShieldAlert className="h-3.5 w-3.5" />}
                {inv.nda_signed ? t("investors.ndaSigned") : t("investors.ndaPending")}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
      {filtered.length === 0 && (
        <p className="py-20 text-center text-slate-400">{t("investors.noResults")}</p>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="investor-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <img src={selected.avatar} alt="" className="h-16 w-16 rounded-full object-cover" />
                  <div>
                    <DialogTitle className="font-display text-2xl text-[#0F172A]">{selected.name}</DialogTitle>
                    <p className="text-sm text-slate-500">{selected.country}</p>
                  </div>
                </div>
              </DialogHeader>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => openTeaser(selected)}
                  data-testid="inv-send-teaser-btn"
                  className="rounded-full bg-[#C6A87C] text-[#0F172A] hover:bg-[#b8986a]"
                >
                  <Send className="mr-2 h-4 w-4" /> {t("investors.sendTeaser")}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="overline text-slate-400">{t("investors.email")}</p>
                  <p className="truncate text-sm font-semibold text-[#0F172A]" data-testid="inv-detail-email">{selected.email || "—"}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="overline text-slate-400">{t("investors.phone")}</p>
                  <p className="truncate text-sm font-semibold text-[#0F172A]" data-testid="inv-detail-phone">{selected.phone || "—"}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="overline text-slate-400">{t("investors.budget")}</p>
                  <p className="text-sm font-semibold text-[#0F172A]">
                    {formatFull(selected.budget_min, selected.currency)}<br />– {formatFull(selected.budget_max, selected.currency)}
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="overline text-slate-400">{t("investors.zones")}</p>
                  <p className="text-sm text-slate-700">{(selected.zones || []).join(", ") || "—"}</p>
                </div>
              </div>
              <div>
                <p className="overline mb-2 text-slate-400">{t("investors.consultations")}</p>
                <ConsultationList ndas={investorNdas(selected)} emptyText={t("investors.noTeaser")} />
              </div>
              <div>
                <p className="overline mb-2 text-slate-400">{t("investors.history")}</p>
                <div className="space-y-3">
                  {(selected.history || []).length === 0 && <p className="text-sm text-slate-400">{t("investors.noHistory")}</p>}
                  {(selected.history || []).map((h, i) => (
                    <div key={i} className="flex gap-3" data-testid={`history-${i}`}>
                      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#C6A87C]" />
                      <div>
                        <p className="text-xs text-slate-400">{h.date}</p>
                        <p className="text-sm text-slate-700">{h.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <InvestorForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSaved={load} />

      {/* Teaser: select opportunity */}
      {teaserFor && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => setTeaserFor(null)}
          data-testid="teaser-select-backdrop"
        />
      )}
      <Dialog open={!!teaserFor} onOpenChange={(v) => !v && setTeaserFor(null)} modal={false}>
        <DialogContent className="max-w-md" data-testid="teaser-select-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0F172A]">{t("investors.sendTeaser")}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {t("investors.teaserDescPre")} <span className="font-medium text-[#0F172A]">{teaserFor?.name}</span>{t("investors.teaserDescPost", { email: teaserFor?.email || t("investors.hisAddress") })}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-slate-700">{t("investors.opportunity")}</Label>
            <Select value={teaserOppId} onValueChange={setTeaserOppId}>
              <SelectTrigger className="mt-1.5 h-10" data-testid="teaser-opp-select"><SelectValue placeholder={t("investors.chooseOpp")} /></SelectTrigger>
              <SelectContent>
                {opps.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`teaser-opp-${o.id}`}>{o.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeaserFor(null)} className="rounded-full" data-testid="teaser-cancel">{t("common.cancel")}</Button>
            <Button onClick={startTeaser} disabled={creatingTeaser} className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="teaser-continue">
              {creatingTeaser ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.continue")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendEmailDialog
        open={!!emailNda}
        onOpenChange={(v) => !v && setEmailNda(null)}
        nda={emailNda}
        msStatus={msStatus}
        onConnect={connectMicrosoft}
        onSent={() => loadNdas()}
      />


      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-investor-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{t("investors.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("investors.deleteDesc", { name: deleting?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-inv-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-full bg-red-600 hover:bg-red-700" data-testid="delete-inv-confirm">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
